import { Bell, CheckCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useTicketNotifications } from '@/hooks/useTicketNotifications';
import type { TicketNotificationItem } from '@/types/tickets';

function tempoRelativo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h} h`;
  const d = Math.floor(h / 24);
  return `há ${d} d`;
}

function tituloItem(n: TicketNotificationItem): string {
  if (n.type === 'MENTION') return `${n.actor_name || 'Alguém'} mencionou você`;
  return 'Atribuído a você';
}

interface Props {
  onOpenTicket: (ticketId: number) => void;
}

export function TicketNotificationsBell({ onOpenTicket }: Props) {
  const { items, count, markAllRead, refresh } = useTicketNotifications();
  // Mostra apenas as não-lidas: ao clicar (que marca como lida via viewed_at) o item
  // sai da lista no próximo refresh, em vez de ficar fixo até o ticket ser concluído.
  const naoLidas = items.filter((n) => !n.is_read);

  return (
    <Popover onOpenChange={(open) => { if (open) refresh(); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" className="relative" title="Notificações">
          <Bell className="h-4 w-4" />
          {count > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-2 -right-2 h-5 min-w-5 px-1 flex items-center justify-center text-xs"
            >
              {count > 99 ? '99+' : count}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between px-4 py-2 border-b">
          <span className="font-medium text-sm">Notificações</span>
          {count > 0 && (
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => markAllRead()}>
              <CheckCheck className="h-3.5 w-3.5 mr-1" /> Marcar todas como lidas
            </Button>
          )}
        </div>
        <div className="max-h-[60vh] overflow-auto">
          {naoLidas.length === 0 ? (
            <p className="text-sm text-muted-foreground px-4 py-6 text-center">Sem notificações</p>
          ) : (
            naoLidas.map((n) => (
              <button
                key={n.key}
                onClick={() => onOpenTicket(n.ticket_id)}
                className="w-full text-left px-4 py-3 border-b hover:bg-muted/50 transition-colors bg-muted/30"
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="text-sm font-medium">{tituloItem(n)}</span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{tempoRelativo(n.created_at)}</span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-muted-foreground">#{n.ticket_id}</span>
                  <span className="text-xs truncate">{n.ticket_title}</span>
                  {n.stage_name && (
                    <Badge
                      variant="outline"
                      className="text-[10px] py-0"
                      style={n.stage_color ? { borderColor: n.stage_color, color: n.stage_color } : undefined}
                    >
                      {n.stage_name}
                    </Badge>
                  )}
                </div>
                {n.excerpt && <p className="text-xs text-muted-foreground mt-1 truncate">{n.excerpt}</p>}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default TicketNotificationsBell;
